import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import type { Publisher, PublisherProperties, Session } from '@opentok/client';
import { CameraOffIcon, CameraOnIcon, ChatOnIcon, HangUpCall } from 'assets/svg-icons';
import MicrophoneOffIcon from 'assets/svg-icons/microphone-off-icon';
import MicrophoneOnIcon from 'assets/svg-icons/microphone-on-icon';
import { LAST_SESSION } from 'const/constant';
import { FORWARDING } from 'const/session-storage';
import { useCall } from 'context/call-context';
import { Action, Index } from 'hooks/models';
import useActivityLogger from 'hooks/useActivityLogger';
import usePermission from 'hooks/usePermission';
import useScreenSize from 'hooks/useScreenSize';

import type { ExpertState } from '../product-expert/product-expert';

import classes from './video-client.module.scss';

interface IVideoClientProps {
  session: Session;
  agentName: string;
  expertName: string;
  closeProductExpertView: (value: Session) => void;
  callState: ExpertState;
  setCallState: React.Dispatch<React.SetStateAction<ExpertState>>;
  settings: ISettings;
  storeName: string;
  onClickResizeButton: () => void;
  halfVideo: boolean;
  isWindowType: boolean;
}

interface ISettings {
  video: boolean;
  audio: boolean;
  chat: boolean;
}

const VideoClient = ({
  session,
  agentName,
  expertName,
  closeProductExpertView,
  callState,
  setCallState,
  settings,
  storeName,
  onClickResizeButton,
  halfVideo,
  isWindowType
}: IVideoClientProps) => {
  const publisherRef = useRef<Publisher>();
  const [muted, setMuted] = useState<boolean>(callState.mode === 'CHAT');
  const [minimized, setMinimized] = useState<boolean>(halfVideo);
  const [callStartTime, setCallStartTime] = useState<Date>(new Date());
  const [videoEnabled, setVideoEnabled] = useState<boolean>(false);
  const { log } = useActivityLogger();
  const { directCallEnabled, setForwarding } = useCall();
  const { onClickPermission, isPermited } = usePermission();
  const { isMobile } = useScreenSize();
  let callStarted = false;
  const publisherProps: PublisherProperties = {
    height: '100%',
    insertMode: 'replace',
    style: { buttonDisplayMode: 'off' },
    width: '100%'
  };
  if (callState.mode !== 'VIDEO') {
    publisherProps.videoSource = null;
  }

  useEffect(() => setMinimized(!halfVideo), [halfVideo]);

  const onClickResizeButtonAction = () => {
    onClickResizeButton();
    setMinimized(!minimized);
  };

  const onclickChatButton = () => {
    if (callState.chatEnabled) {
      setCallState((prevState: ExpertState) => ({
        ...prevState,
        chatEnabled: false,
        newMessageCount: 0
      }));
    } else {
      setCallState((prevState: ExpertState) => ({
        ...prevState,
        chatEnabled: true,
        minimized: true,
        newMessageCount: 0
      }));
    }
  };

  const onclickMicButton = () => {
    if (callState.mode === 'CHAT' || !settings.audio) {
      return;
    }
    if (muted) {
      setMuted(false);
      publisherRef.current?.publishAudio(true);
    } else {
      setMuted(true);
      publisherRef.current?.publishAudio(false);
    }
  };

  const onclickVideoButton = () => {
    if (!isPermited.video) {
      onClickPermission();
      return;
    }
    if (callState.mode === 'CHAT' || !settings.video) {
      return;
    }
    if (videoEnabled) {
      setVideoEnabled(false);
      publisherRef.current?.publishVideo(false);
    } else {
      setVideoEnabled(true);
      publisherRef.current?.publishVideo(true);
    }
  };

  const onEndCall = (session: Session) => {
    sessionStorage.removeItem(FORWARDING);
    setForwarding(false);
    closeProductExpertView(session);
    if (!callStarted) return;

    void log({
      action: Action.CALL_END_BUTTON_CLICKED,
      agentUsername: agentName,
      description: 'customer video call end.',
      index: Index.ACTIVITY,
      label: 'CALL END - BUTTON CLICKED',
      sessionID: session.sessionId
    });
  };

  const swapCamera = () => {
    publisherRef.current?.cycleVideo();
  };

  const handleStreamCreated = () => {
    session.on('streamCreated', (event) => {
      console.debug('OT => streamCreated', event);
      const subscriber = session.subscribe(
        event.stream,
        'subscriber',
        {
          height: '100%',
          insertMode: 'replace',
          style: { buttonDisplayMode: 'off' },
          width: '100%'
        },
        (err) => {
          if (err) {
            console.error('OT => Failed to subscribe agent stream', err);
            void log({
              action: Action.ERROR,
              agentUsername: agentName,
              description: `Failed to subscribe agent stream error name: ${err.name}, error Message: ${err.message}`,
              index: Index.ACTIVITY,
              label: 'ERROR',
              sessionID: session.sessionId
            });
          } else console.info('OT => Agent stream subscribe successfully');
        }
      );
      subscriber.on({
        videoDisabled: () => {
          console.debug('OT => Agent video is blocked.');
        },
        videoEnabled: () => {
          console.debug('OT => Agent video is unblocked.');
        }
      });

      if (subscriber.getAudioVolume() < 80) {
        subscriber.setAudioVolume(80);
      }
      void log({
        action: Action.EXPERT_MIC_VOLUME,
        agentUsername: agentName,
        data: {
          micLevel: subscriber.getAudioVolume()
        },
        description: 'Expert(Subscriber) mic volume level is' + subscriber.getAudioVolume(),
        index: Index.ACTIVITY,
        label: 'CALL HISTORY',
        sessionID: session.sessionId
      });
    });
  };

  const handleStreamDestroyed = () => {
    session.on('streamDestroyed', async (event) => {
      console.debug('OT => streamDestroyed', event);
      closeProductExpertView(session);
    });
  };

  const initGtm = () => {
    // @ts-ignore
    if (window && window.dataLayer) {
      // @ts-ignore
      window.dataLayer.push({
        action: ' START CALL',
        actor: 'customer',
        data: JSON.stringify({
          sessionId: session.sessionId,
          startTime: callStartTime,
          type: directCallEnabled ? 'BOOKING' : 'WALK IN',
          username: agentName
        }),
        description: 'customer video call history.',
        event: 'start_call',
        label: 'CALL HISTORY',
        sessionId: session.sessionId,
        store: storeName
      });
    }
  };

  const handlePublish = (publisher: Publisher) => {
    let localPublisher = publisher;
    if (isPermited.video) {
      const publisherOptions: PublisherProperties = {
        height: '100%',
        insertMode: 'replace',
        publishAudio: callState.mode !== 'CHAT',
        publishVideo: true,
        style: { buttonDisplayMode: 'off' },
        width: '100%'
      };
      localPublisher = OT.initPublisher('publisher', publisherOptions);
      localPublisher.publishVideo(true);
    }
    publisherRef.current = localPublisher;
    session.publish(localPublisher, (error) => {
      if (error) {
        console.error('OT => There was an error publishing: ', error.name, error.message);
        void log({
          action: Action.ERROR,
          agentUsername: agentName,
          description: `There was an error publishing: ${error.name}, error Message: ${error.message}`,
          index: Index.ACTIVITY,
          label: 'ERROR',
          sessionID: session.sessionId
        });
        return;
      }
      console.debug('OT => start publishing');
    });
  };

  const initHistory = () => {
    if (window && window.history) {
      const serializableObj = {
        event: 'StartCall',
        sessionId: session.sessionId
      };
      window.history.pushState(serializableObj, '', new URL(window.location.href));
    }
  };

  const initializeSession = () => {
    handleStreamCreated();
    handleStreamDestroyed();

    // Create a publisher
    const publisher = OT.initPublisher('publisher', publisherProps, (error) => {
      if (error) {
        console.error('OT => There was an error initializing the publisher: ', error.name, error.message);
        void log({
          action: Action.ERROR,
          agentUsername: agentName,
          description: `There was an error initializing the publisher: ${error.name}, error Message: ${error.message}`,
          index: Index.ACTIVITY,
          label: 'ERROR',
          sessionID: session.sessionId
        });
        if (error.name != 'OT_HARDWARE_UNAVAILABLE') {
          return;
        } else console.info('OT => Publisher initialize successfully');
      }

      if (settings) {
        if (!settings.audio) {
          publisher.publishAudio(false);
        }
        if (!settings.video && callState.mode === 'VIDEO') {
          publisher.publishVideo(false);
        }
      }
      publisherRef.current = publisher;
      handlePublish(publisher);
      callStarted = true;
      initHistory();
      initGtm();
      void log({
        action: Action.START_CALL,
        agentUsername: agentName,
        description: 'customer video call history.',
        index: Index.ACTIVITY,
        label: 'CALL HISTORY',
        sessionID: session.sessionId
      });
    });
  };

  useEffect(() => {
    if (settings) {
      setMuted(!settings.audio);
      setVideoEnabled(isPermited.video && settings.video);
    }
    if (publisherRef.current) {
      handlePublish(publisherRef.current);
    }
  }, [isPermited.video]);

  useEffect(() => {
    if (callState.mode === 'CHAT') return;
    initializeSession();
    setCallStartTime(new Date());
    localStorage.setItem(LAST_SESSION, session.sessionId);

    return () => {
      session.disconnect();
      localStorage.removeItem(LAST_SESSION);
      document.body.style.overflow = 'auto';
      if (!callStarted) return;
      const serializableObj = {
        event: 'EndCall',
        sessionId: session.sessionId
      };
      window.history.pushState(serializableObj, '', new URL(window.location.href));
      void log({
        action: Action.END_CALL,
        agentUsername: agentName,
        description: 'customer video call history.',
        index: Index.ACTIVITY,
        label: 'CALL HISTORY',
        sessionID: session.sessionId
      });
    };
  }, []);

  useEffect(() => {
    window.scroll(0, 0);
    setTimeout(() => {
      document.body.style.overflow = 'hidden';
    }, 500);
  }, []);

  return (
    <div className={classes.cwVideoSide}>
      <p className={classes.cwAgentName}>{expertName}</p>
      {callState.mode !== 'CHAT' && (
        <div style={{ height: '100%', width: '100%' }}>
          <div className={classes.cwCustomerVideo}>
            <div id="subscriber" />
          </div>
          <div className={classes.cwAgentVideo}>
            {isMobile && (
              <div className={classes.cwCameraSwitch}>
                <div onClick={swapCamera}>
                  <svg
                    width="35"
                    height="33"
                    viewBox="0 0 35 33"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M35 5.40859V8.55313C35 10.287 33.4667 11.6977 31.582 11.6977H28.1641C27.4089 11.6977 26.7969 11.1346 26.7969 10.4398C26.7969 9.7451 27.4089 9.18203 28.1641 9.18203H30.2106C27.5731 5.08898 22.7452 2.51562 17.5 2.51562C16.7448 2.51562 16.1328 1.95256 16.1328 1.25781C16.1328 0.563068 16.7448 0 17.5 0C20.9337 0 24.2582 0.914862 27.1141 2.64534C29.1932 3.90511 30.943 5.54346 32.2656 7.45377V5.40859C32.2656 4.71385 32.8777 4.15078 33.6328 4.15078C34.388 4.15078 35 4.71385 35 5.40859ZM22.6456 11.0059H21.9233C21.8811 11.0059 21.8424 10.983 21.8251 10.9476L21.6435 10.5838C21.1815 9.65764 20.1674 9.0592 19.0597 9.0592H16.3304C15.2228 9.0592 14.2086 9.65764 13.7469 10.5836L13.5653 10.9476C13.5477 10.983 13.509 11.0059 13.4668 11.0059H12.6281C11.0173 11.0059 9.70703 12.2113 9.70703 13.693V19.3217C9.70703 20.8035 11.0173 22.0088 12.6278 22.0088H22.6456C24.2561 22.0088 25.5664 20.8035 25.5664 19.3217V13.693C25.5664 12.2113 24.2561 11.0059 22.6456 11.0059ZM22.832 19.3217C22.832 19.4163 22.7485 19.4931 22.6456 19.4931H12.6278C12.525 19.4931 12.4414 19.4163 12.4414 19.3217V13.693C12.4414 13.5984 12.525 13.5215 12.6278 13.5215H13.4665C14.5742 13.5215 15.5883 12.923 16.0503 11.9969L16.2319 11.6328C16.2495 11.5974 16.288 11.5748 16.3304 11.5748H19.0597C19.1019 11.5748 19.1404 11.5974 19.158 11.6328L19.3396 11.9971C19.8018 12.923 20.8157 13.5215 21.9233 13.5215H22.6456C22.7485 13.5215 22.832 13.5984 22.832 13.693V19.3217ZM17.6367 17.9867C18.7695 17.9867 19.6875 17.1421 19.6875 16.1C19.6875 15.0579 18.7695 14.2133 17.6367 14.2133C16.504 14.2133 15.5859 15.0579 15.5859 16.1C15.5859 17.1421 16.504 17.9867 17.6367 17.9867ZM18.3139 32.1855C19.0685 32.1565 19.6544 31.5704 19.6229 30.8764C19.5914 30.1823 18.9548 29.6429 18.1999 29.6723C17.99 29.6802 17.7774 29.6844 17.5684 29.6844C12.3295 29.6844 7.4963 27.1093 4.85832 23.018H6.83594C7.59109 23.018 8.20312 22.4549 8.20312 21.7602C8.20312 21.0654 7.59109 20.5023 6.83594 20.5023H3.41797C1.53328 20.5023 0 21.913 0 23.6469V26.7914C0 27.4862 0.61203 28.0492 1.36719 28.0492C2.12234 28.0492 2.73438 27.4862 2.73438 26.7914V24.646C4.07219 26.6099 5.85861 28.2914 7.9879 29.5751C10.8363 31.2923 14.1491 32.2 17.5684 32.2C17.8154 32.2 18.0664 32.1951 18.3139 32.1855ZM0 16.1C0 15.4053 0.61203 14.8422 1.36719 14.8422C2.12234 14.8422 2.73438 15.4053 2.73438 16.1C2.73438 16.7947 2.12234 17.3578 1.36719 17.3578C0.61203 17.3578 0 16.7947 0 16.1ZM2.11914 10.2512C1.36398 10.2512 0.751953 10.8142 0.751953 11.509C0.751953 12.2037 1.36398 12.7668 2.11914 12.7668C2.8743 12.7668 3.48633 12.2037 3.48633 11.509C3.48633 10.8142 2.8743 10.2512 2.11914 10.2512ZM3.07617 7.3582C3.07617 6.66346 3.6882 6.10039 4.44336 6.10039C5.19852 6.10039 5.81055 6.66346 5.81055 7.3582C5.81055 8.05295 5.19852 8.61602 4.44336 8.61602C3.6882 8.61602 3.07617 8.05295 3.07617 7.3582ZM7.99805 2.83008C7.24289 2.83008 6.63086 3.39315 6.63086 4.08789C6.63086 4.78264 7.24289 5.3457 7.99805 5.3457C8.7532 5.3457 9.36523 4.78264 9.36523 4.08789C9.36523 3.39315 8.7532 2.83008 7.99805 2.83008ZM11.1426 1.94961C11.1426 1.25486 11.7546 0.691797 12.5098 0.691797C13.2649 0.691797 13.877 1.25486 13.877 1.94961C13.877 2.64435 13.2649 3.20742 12.5098 3.20742C11.7546 3.20742 11.1426 2.64435 11.1426 1.94961ZM33.6328 17.3578C34.388 17.3578 35 16.7947 35 16.1C35 15.4053 34.388 14.8422 33.6328 14.8422C32.8777 14.8422 32.2656 15.4053 32.2656 16.1C32.2656 16.7947 32.8777 17.3578 33.6328 17.3578ZM34.248 20.5652C34.248 21.26 33.636 21.823 32.8809 21.823C32.1257 21.823 31.5137 21.26 31.5137 20.5652C31.5137 19.8705 32.1257 19.3074 32.8809 19.3074C33.636 19.3074 34.248 19.8705 34.248 20.5652ZM30.6934 25.848C31.4485 25.848 32.0605 25.285 32.0605 24.5902C32.0605 23.8955 31.4485 23.3324 30.6934 23.3324C29.9382 23.3324 29.3262 23.8955 29.3262 24.5902C29.3262 25.285 29.9382 25.848 30.6934 25.848ZM28.7109 27.8605C28.7109 28.5553 28.0989 29.1184 27.3438 29.1184C26.5886 29.1184 25.9766 28.5553 25.9766 27.8605C25.9766 27.1658 26.5886 26.6027 27.3438 26.6027C28.0989 26.6027 28.7109 27.1658 28.7109 27.8605ZM23.0371 31.3195C23.7923 31.3195 24.4043 30.7565 24.4043 30.0617C24.4043 29.367 23.7923 28.8039 23.0371 28.8039C22.282 28.8039 21.6699 29.367 21.6699 30.0617C21.6699 30.7565 22.282 31.3195 23.0371 31.3195Z"
                      fill="white"
                    />
                  </svg>
                </div>
              </div>
            )}
            <div id="publisher" />
          </div>
        </div>
      )}
      <div className={classes.cwVideoActionIcons}>
        {callState.mode !== 'CHAT' && (
          <>
            <div
              className={classes.cwActionButton}
              style={{
                background: callState.chatEnabled ? 'white' : 'gray'
              }}
              onClick={onclickChatButton}
            >
              <ChatOnIcon />
            </div>
            <div
              className={classes.cwActionButton}
              onClick={onclickVideoButton}
            >
              {videoEnabled ? <CameraOnIcon /> : <CameraOffIcon />}
            </div>
            <div
              className={classes.cwActionButton}
              onClick={onclickMicButton}
            >
              {muted ? <MicrophoneOffIcon /> : <MicrophoneOnIcon />}
            </div>
          </>
        )}

        <div
          className={classes.cwActionButton}
          style={{ background: 'red' }}
          onClick={() => onEndCall(session)}
        >
          <HangUpCall />
        </div>
        {(isWindowType || (callState.mode !== 'CHAT' && isMobile)) && (
          <div
            style={{ color: 'white', fontSize: '2.5em' }}
            onClick={() => onClickResizeButtonAction()}
          >
            {minimized && (
              <span className="cwicon-CTA-MainNav_ExpandVideo">
                <span
                  className="cwicon-CTA-MainNav_ExpandVideo-path1"
                  style={{ fontSize: '35px' }}
                />
                <span
                  className="cwicon-CTA-MainNav_ExpandVideo-path2"
                  style={{ fontSize: '35px' }}
                />
                <span
                  className="cwicon-CTA-MainNav_ExpandVideo-path3"
                  style={{ fontSize: '35px' }}
                />
              </span>
            )}
            {!minimized && (
              <span className="cwicon-CTA-MainNav_MinimizeVideo">
                <span
                  className="cwicon-CTA-MainNav_MinimizeVideo-path1"
                  style={{ fontSize: '35px' }}
                />
                <span
                  className="cwicon-CTA-MainNav_MinimizeVideo-path2"
                  style={{ fontSize: '35px' }}
                />
                <span
                  className="cwicon-CTA-MainNav_MinimizeVideo-path3"
                  style={{ fontSize: '35px' }}
                />
              </span>
            )}
          </div>
        )}
        <div
          className="center"
          style={{ color: 'white', fontSize: '1rem' }}
        >
          <a
            className="cw-logo-path"
            href="https://conferwith.io/?utm_source=platform&utm_medium=survey&utm_content=homepage"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="cwicon-GRAPHIC-PoweredBy_White" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default VideoClient;
